import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, DollarSign, Send, UserPlus, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton, StatCard } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { Avatar } from '@/components/ui/avatar';
import { Modal } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { api, cn, formatCurrency, formatDate } from '@/lib/utils';
import { addMonths } from '@maintenance-tracker/shared';
import { useState } from 'react';

interface OpportunityItem {
  id: string;
  contactId: string;
  contactName: string;
  contactEmail: string | null;
  stage: 'identified' | 'offered' | 'converted' | 'lost';
  estimatedValue: number | null;
  offeredPlan: string | null;
  lastServiceDate: string | null;
}

interface FunnelStats {
  identified: number;
  offered: number;
  converted: number;
  lost: number;
  conversionRate: number;
  convertedRevenue: number;
}

const STAGES = [
  { key: 'identified', label: 'Identified', tone: 'default' as const },
  { key: 'offered', label: 'Offered', tone: 'warning' as const },
  { key: 'converted', label: 'Converted', tone: 'success' as const },
];

export function OpportunitiesPage() {
  const qc = useQueryClient();
  const [convertOpen, setConvertOpen] = useState<OpportunityItem | null>(null);
  const [convertForm, setConvertForm] = useState({
    plan_name: 'Annual maintenance membership',
    amount: 399,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: addMonths(new Date().toISOString().slice(0, 10), 12),
    interval_months: 12,
    currency: 'USD' as const,
  });

  const { data: funnel, isLoading: funnelLoading } = useQuery({
    queryKey: ['opportunities-funnel'],
    queryFn: () => api<FunnelStats>('/api/v1/opportunities/funnel?period=90'),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['opportunities'],
    queryFn: () => api<{ items: OpportunityItem[] }>('/api/v1/opportunities'),
  });

  const offer = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/opportunities/${id}/offer`, {
        method: 'POST',
        body: JSON.stringify({ offered_plan: 'Maintenance membership' }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunities'] });
      qc.invalidateQueries({ queryKey: ['opportunities-funnel'] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const convert = useMutation({
    mutationFn: ({ id, body }: { id: string; body: typeof convertForm & { contact_id: string; currency: 'USD' } }) =>
      api(`/api/v1/opportunities/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setConvertOpen(null);
      qc.invalidateQueries({ queryKey: ['opportunities'] });
      qc.invalidateQueries({ queryKey: ['opportunities-funnel'] });
      qc.invalidateQueries({ queryKey: ['agreements'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const openItems = (data?.items ?? []).filter((i) => i.stage === 'identified' || i.stage === 'offered');

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-sm text-text-secondary">
          Membership conversion tracking is available on the Pro plan. Upgrade in billing to unlock the funnel.
        </p>
        <Link to="/settings/billing" className="mt-3 inline-block text-sm font-medium text-brand hover:underline">
          View plans
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Membership conversions"
        description="Turn one-off service customers into recurring maintenance members — track the funnel and nudge conversions."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        {STAGES.map((stage, idx) => {
          const count = funnel?.[stage.key as keyof FunnelStats] as number | undefined;
          return (
            <div key={stage.key} className="relative">
              <StatCard
                label={stage.label}
                value={count ?? 0}
                icon={idx === 0 ? UserPlus : idx === 1 ? Send : DollarSign}
                tone={stage.tone}
                loading={funnelLoading}
              />
              {idx < STAGES.length - 1 && (
                <ArrowRight className="absolute -right-3 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-text-muted sm:block" />
              )}
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard
          label="Conversion rate"
          value={`${funnel?.conversionRate ?? 0}%`}
          icon={Wrench}
          tone="default"
          hint="Last 90 days"
          loading={funnelLoading}
        />
        <StatCard
          label="Revenue generated"
          value={formatCurrency(funnel?.convertedRevenue ?? 0)}
          icon={DollarSign}
          tone="success"
          hint="From converted memberships"
          loading={funnelLoading}
        />
      </div>

      <Card title="Ready to convert" description="Customers with recent service work but no active maintenance agreement." noPadding>
        {isLoading ? (
          <div className="divide-y divide-border px-5 py-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="mb-3 h-14 w-full" />
            ))}
          </div>
        ) : openItems.length ? (
          <div className="divide-y divide-border">
            {openItems.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                <Avatar name={item.contactName} className="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-text-primary">{item.contactName}</p>
                  <p className="text-[13px] text-text-secondary">
                    Last service {item.lastServiceDate ? formatDate(item.lastServiceDate) : 'recently'}
                    {item.estimatedValue ? ` · ~${formatCurrency(item.estimatedValue)}/yr potential` : ''}
                  </p>
                  {item.stage === 'offered' && (
                    <p className="mt-0.5 text-[12px] text-brand">In membership campaign · {item.offeredPlan}</p>
                  )}
                </div>
                <span
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-medium capitalize',
                    item.stage === 'offered' ? 'bg-[#FEF9C3] text-[#854D0E]' : 'bg-bg-subtle text-text-secondary',
                  )}
                >
                  {item.stage}
                </span>
                <div className="flex flex-wrap gap-2">
                  {item.stage === 'identified' && (
                    <Button variant="secondary" size="sm" loading={offer.isPending} onClick={() => offer.mutate(item.id)}>
                      Offer membership
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      setConvertOpen(item);
                      setConvertForm((f) => ({
                        ...f,
                        plan_name: item.offeredPlan ?? 'Annual maintenance membership',
                        amount: item.estimatedValue ?? 399,
                        end_date: addMonths(f.start_date, 12),
                      }));
                    }}
                  >
                    Convert to agreement
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={UserPlus}
            title="No membership leads yet"
            description="Import customers with service history or connect an integration — we'll flag anyone without a maintenance plan."
          />
        )}
      </Card>

      <Modal
        open={!!convertOpen}
        onClose={() => setConvertOpen(null)}
        title="Convert to maintenance agreement"
        description="Create the membership and mark this opportunity converted."
      >
        {convertOpen && (
          <div className="space-y-4">
            <Input
              label="Plan name"
              value={convertForm.plan_name}
              onChange={(e) => setConvertForm({ ...convertForm, plan_name: e.target.value })}
            />
            <Input
              label="Annual amount"
              type="number"
              prefix="$"
              value={convertForm.amount || ''}
              onChange={(e) => setConvertForm({ ...convertForm, amount: Number(e.target.value) || 0 })}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start date"
                type="date"
                value={convertForm.start_date}
                onChange={(e) => setConvertForm({ ...convertForm, start_date: e.target.value })}
              />
              <Input
                label="Renewal date"
                type="date"
                value={convertForm.end_date}
                onChange={(e) => setConvertForm({ ...convertForm, end_date: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setConvertOpen(null)}>
                Cancel
              </Button>
              <Button
                loading={convert.isPending}
                onClick={() =>
                  convert.mutate({
                    id: convertOpen.id,
                    body: { ...convertForm, contact_id: convertOpen.contactId, currency: 'USD' },
                  })
                }
              >
                Create agreement
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
